import { Modal } from "antd";
import Title from "antd/es/typography/Title";
import { FormikProvider, useFormik } from "formik";
import { CompaniesSelect } from "pages/SupplyChain/MaterialManagement/ui";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetDimensionUnitsQuery } from "shared/api";
import { adaptDimensionUnitsData } from "shared/adapters";
import {
  expandDimensionUnits,
  materialManagementInitialValues,
  ModalSizeValue,
} from "shared/constants";
import { noop } from "shared/helpers";
import { useMaterialManagementRequests } from "shared/hooks";
import { CreateMaterialSchema } from "shared/schemas";
import { AdaptedValues, SubmitCreateMaterialValues } from "shared/types";
import { FormInput, FormSelect } from "shared/ui";

interface CreateMaterialManagementItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  materialCategoriesAdapted: AdaptedValues[];
}

const CreateMaterialManagementItemModal: FC<
  CreateMaterialManagementItemModalProps
> = ({ isOpen, onClose, materialCategoriesAdapted }) => {
  const { t } = useTranslation();

  const { data: dimensionUnits } = useGetDimensionUnitsQuery({
    expand: expandDimensionUnits,
  });

  const unitsWithLabel = useMemo(
    () => adaptDimensionUnitsData(dimensionUnits || []),
    [dimensionUnits]
  );

  const addMaterialItemFormik = useFormik<SubmitCreateMaterialValues>({
    initialValues: materialManagementInitialValues,
    validateOnMount: false,
    validateOnBlur: true,
    validationSchema: CreateMaterialSchema,
    onSubmit: noop,
  });

  const {
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    errors,
    isValid,
    resetForm,
  } = addMaterialItemFormik;

  const closeModalAndResetForm = () => {
    onClose();
    resetForm();
  };

  const { contextHolder, createMaterialItem } = useMaterialManagementRequests({
    closeAddMaterialModal: closeModalAndResetForm,
  });

  const handleCreateMaterialItem = () => {
    createMaterialItem({
      name: values?.name,
      material_category: values?.material_category.id!,
      suppliers: values.suppliers!,
      target_quantity_in_stock: values.target_quantity_in_stock!,
      n_packaging_items: values.n_packaging_items!,
      expanded_label_name: values.expanded_label_name!,
      invoice_mapping_name: values.invoice_mapping_name!,
      unit: typeof values.unit === "number" ? values.unit : null,
    });
  };

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  return (
    <FormikProvider value={addMaterialItemFormik}>
      {contextHolder}
      <Modal
        open={isOpen}
        title={t("material-management.add-modal-name")}
        width={ModalSizeValue.X_MEDIUM}
        okButtonProps={{
          disabled: !isValid,
          style: {
            background: `${themeColor}`,
          },
        }}
        onOk={handleCreateMaterialItem}
        onCancel={onClose}
      >
        <Title level={5}>{t("material-management.name")}</Title>

        <FormInput
          value={values.name}
          fieldName="name"
          placeholder={t("material-management.create-modal-name-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.name?.toString()}
          isError={!!errors.name}
        />

        <Title level={5}>{t("material-management.expanded-name")}</Title>

        <FormInput
          value={values.expanded_label_name}
          fieldName="expanded_label_name"
          placeholder={t(
            "material-management.create-modal-expanded-name-placeholder"
          )}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.expanded_label_name?.toString()}
          isError={!!errors.expanded_label_name}
        />

        <Title level={5}>{t("material-management.invoice-mapping-name")}</Title>

        <FormInput
          value={values.invoice_mapping_name}
          fieldName="invoice_mapping_name"
          placeholder={t(
            "material-management.invoice-mapping-name-placeholder"
          )}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.invoice_mapping_name?.toString()}
          isError={!!errors.invoice_mapping_name}
        />

        <Title level={5}>{t("material-management.category-name")}</Title>

        <FormSelect
          value={values?.material_category?.id}
          placeholder={t(
            "material-management.create-modal-material-category-placeholder"
          )}
          fieldName="material_category.id"
          options={materialCategoriesAdapted}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors.material_category?.id?.toString()}
          isError={!!errors.material_category?.id}
          showSearch
        />

        <Title level={5}>
          {t("material-management.target-quantity-in-stock")}
        </Title>

        <FormInput
          value={values.target_quantity_in_stock}
          fieldName="target_quantity_in_stock"
          placeholder={t(
            "material-management.target-quantity-in-stock-placeholder"
          )}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.target_quantity_in_stock?.toString()}
          isError={!!errors.target_quantity_in_stock}
          numberType
        />

        <Title level={5}>{t("material-management.n-packaging-items")}</Title>

        <FormInput
          value={values.n_packaging_items}
          fieldName="n_packaging_items"
          placeholder={t("material-management.n-packaging-items-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.n_packaging_items?.toString()}
          isError={!!errors.n_packaging_items}
          numberType
        />

        <Title level={5}>{t("material-management.unit")}</Title>

        <FormSelect
          value={values.unit}
          placeholder={t("material-management.unit")}
          fieldName="unit"
          optionsWithLabels={unitsWithLabel}
          withLabels
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          showSearch
        />

        <CompaniesSelect />
      </Modal>
    </FormikProvider>
  );
};

export { CreateMaterialManagementItemModal };
